// chatApi — typed fetchers + SSE consumer for /v1/ai/*.
//
// Spec: docs/specs/ai-chat-guided-tour.md §9 API contracts.
//
// All endpoints require read:user:self (gateway-enforced). Cookies
// (nexus_session) are sent via credentials:"include".

import { apiFetch } from "../../lib/api-client.js";

// ─── Types ──────────────────────────────────────────────────────────────────

export interface AiAvailability {
  available: boolean;
  reason: string | null;
  /** Per-tenant monthly token usage (replaces per-user daily message
   *  count from before 2026-05). Spec: docs/specs/ai-token-quota.md §6.1. */
  used_month_tokens: number;
  cap_month_tokens: number;
  /** 0–100 integer; convenience for the bubble's percentage strip. */
  percent_used: number;
  /** ISO 8601 — next calendar-month start (when usage resets to 0). */
  reset_at: string;
  /** Legacy fields kept for backwards-compat — server echoes the
   *  monthly figures into these so old frontend caches don't render
   *  「0 / 200」. Will be removed in a later cleanup. */
  used_today: number;
  cap_today: number;
  correlation_id: string | null;
}

export interface AiThreadRow {
  id: string;
  user_id: string;
  tenant_id: string | null;
  title: string | null;
  openai_last_response_id: string | null;
  message_count: number;
  total_tokens_used: number;
  created_at: string;
  updated_at: string;
  archived_at: string | null;
}

export interface AiMessageRow {
  id: string;
  thread_id: string;
  role: "user" | "assistant" | "tool" | "system";
  content: string;
  tool_calls: string | null;
  tool_call_id: string | null;
  tokens_in: number | null;
  tokens_out: number | null;
  screen_context: string | null;
  created_at: string;
}

export interface AiThreadDetail {
  thread: AiThreadRow;
  messages: AiMessageRow[];
  correlation_id: string | null;
}

// ─── Errors ─────────────────────────────────────────────────────────────────

export class AiChatApiError extends Error {
  status: number;
  code: string;
  correlationId: string | null;
  detail?: unknown;
  constructor(status: number, code: string, correlationId: string | null, detail?: unknown) {
    super(code);
    this.status = status;
    this.code = code;
    this.correlationId = correlationId;
    this.detail = detail;
  }
}

export function friendlyChatError(err: unknown): string {
  if (err instanceof AiChatApiError) {
    if (err.status === 401) return "登入狀態已逾時，請重新登入後再試。";
    if (err.status === 403 && err.code === "feature_disabled")
      return "您的單位目前未啟用 SENTRY 助手。請聯絡管理員。";
    if (err.status === 403 && err.code === "quota_zero")
      return "您的單位目前未開放 SENTRY 助手用量。";
    if (err.status === 429 && err.code === "quota_exhausted") {
      const d = (err.detail ?? {}) as { used_month_tokens?: number; cap_month_tokens?: number };
      const used = d.used_month_tokens ?? 0;
      const cap = d.cap_month_tokens ?? 0;
      const percent = cap > 0 ? Math.min(100, Math.round((used / cap) * 100)) : 100;
      return `本月 AI 用量已達上限（${percent}%）。下月 1 日重置。`;
    }
    if (err.status === 404) return "找不到此對話（可能已被刪除）。";
    if (err.status === 400 && err.code === "empty_text") return "請輸入訊息內容。";
    return `SENTRY 助手暫時無法使用（HTTP ${err.status}・code=${err.code}・cid=${err.correlationId ?? "n/a"}）。`;
  }
  return "連線中斷，請檢查網路後再試。";
}

async function asError(r: Response): Promise<AiChatApiError> {
  let code = "request_failed";
  let cid: string | null = r.headers.get("x-correlation-id");
  let detail: unknown;
  try {
    const body = (await r.json()) as Record<string, unknown>;
    code = (body.error as string) ?? code;
    cid = (body.correlation_id as string) ?? cid;
    detail = body;
  } catch { /* tolerate */ }
  return new AiChatApiError(r.status, code, cid, detail);
}

// ─── Endpoints ──────────────────────────────────────────────────────────────

export async function getAiAvailability(): Promise<AiAvailability> {
  const r = await apiFetch("/v1/ai/availability", { credentials: "include" });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as AiAvailability;
}

export async function listAiThreads(limit = 50): Promise<{ items: AiThreadRow[]; correlation_id: string | null }> {
  const r = await apiFetch(`/v1/ai/threads?limit=${limit}`, { credentials: "include" });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as { items: AiThreadRow[]; correlation_id: string | null };
}

/** AI mode "工作" sidebar feed. Spec docs/specs/ai-mode-operations-console.md
 *  §4.2. AM-1 projection over user_ai_threads with one_line_excerpt; the
 *  shape allows future kinds (recent plan applies, pinned workflows)
 *  via the discriminator. */
export interface WorkItem {
  kind: "thread";
  id: string;
  title: string | null;
  one_line_excerpt: string | null;
  message_count: number;
  total_tokens_used: number;
  created_at: string;
  updated_at: string;
}

export async function listRecentWorks(limit = 15): Promise<{ works: WorkItem[] }> {
  const r = await apiFetch(`/v1/ai/works/recent?limit=${limit}`, { credentials: "include" });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as { works: WorkItem[] };
}

export async function createAiThread(title?: string): Promise<AiThreadRow> {
  const r = await apiFetch("/v1/ai/threads", {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(title ? { title } : {}),
  });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as AiThreadRow;
}

export async function getAiThread(id: string): Promise<AiThreadDetail> {
  const r = await apiFetch(`/v1/ai/threads/${encodeURIComponent(id)}`, { credentials: "include" });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as AiThreadDetail;
}

export async function deleteAiThread(id: string): Promise<void> {
  const r = await apiFetch(`/v1/ai/threads/${encodeURIComponent(id)}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!r.ok) throw await asError(r);
}

// ─── Preferences ────────────────────────────────────────────────────────────

export type AiPrefKey = "lang" | "screen_context_level" | "ai_chat_dismissed" | "ai_mode";
export type AiModeValue = "ai" | "professional";
export interface AiPrefs {
  lang?: string;                          // 'auto' | 'zh-TW' | 'en' | 'ja' | 'zh-CN'
  screen_context_level?: string;          // 'minimal' | 'standard' | 'rich'
  ai_chat_dismissed?: boolean;
  /** Dashboard primary mode (spec docs/specs/ai-mode-operations-console.md).
   *  - "ai" (default for new users):  AI 模式 — Works sidebar + embedded chat in AI 導覽
   *  - "professional": existing full sidebar + floating ChatBubble + every page */
  ai_mode?: AiModeValue;
}

export async function getAiPreferences(): Promise<AiPrefs> {
  const r = await apiFetch("/v1/ai/preferences", { credentials: "include" });
  if (!r.ok) throw await asError(r);
  const body = (await r.json()) as { prefs: AiPrefs };
  return body.prefs ?? {};
}

export async function setAiPreference<K extends AiPrefKey>(key: K, value: AiPrefs[K]): Promise<void> {
  const r = await apiFetch("/v1/ai/preferences", {
    method: "PUT",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ key, value }),
  });
  if (!r.ok) throw await asError(r);
}

// ─── SSE: POST /v1/ai/threads/:id/messages ─────────────────────────────────

export interface SendMessageRequest {
  threadId: string;
  text: string;
  /** Layer C-min screen-context snapshot (PR #5 will populate; PR #3 sends null). */
  screen_context?: Record<string, unknown> | null;
  langHint?: string;
}

export interface SseToolCall {
  call_id: string;
  name: string;
  args: Record<string, unknown>;
  known: boolean;
}

export interface SseFinal {
  message_id: string;
  user_message_id: string | null;
  content: string;
  tool_calls: SseToolCall[];
  pending_tool_calls: boolean;
  tokens_in: number;
  tokens_out: number;
  model?: string;
  response_id?: string | null;
}

export interface AiTourStep {
  tour_id: string;
  action: "click" | "input" | "select" | "navigate" | "review";
  hint: string;
  input_value?: string;
}

export interface AiTourProposal {
  /** function_call id; used as the tool_call_id when reporting outcome back. */
  call_id: string;
  /** ai-api-generated; used by /v1/ai/tours/:run_id/start /step /complete. */
  run_id: string;
  title: string;
  summary: string;
  steps: AiTourStep[];
}

export interface QuickNavItem {
  label: string;
  route: string;
  /** Optional data-tour-id. After navigate, UI waits for this element
   *  to mount then programmatically clicks it (used to open drawers). */
  then_click?: string;
  icon?: string;
}

export interface QuickNavProposal {
  call_id: string;
  items: QuickNavItem[];
}

/** AM-5c — AI 導覽 drawer focus directive. Browser narrows topology
 *  rendering to `entity_ids` + their ancestor chain back to the
 *  tenant root, auto-opens the drawer to topology view, and surfaces
 *  `reason` as a header subtitle so the operator understands why the
 *  canvas filtered. */
export interface FocusTopologyProposal {
  call_id: string;
  entity_ids: string[];
  reason: string;
}

/** AM-7 — open a known create-form inside the AI 導覽 drawer. The
 *  operator fills + saves; on success the browser posts a tool_result
 *  with the created entity_id so the AI can ack and chain follow-up
 *  questions. */
export type DrawerFormKind = "create_location" | "create_client" | "create_task" | "issue_binding_code";
export interface DrawerFormProposal {
  call_id: string;
  kind: DrawerFormKind;
  prefill: Record<string, unknown>;
  reason: string;
}

/** Inline N+1 choice card rendered on the assistant message bubble.
 *  N concrete options the AI listed + a 「其他」 fallback (when
 *  allow_other) that reveals a free-text input. On operator selection
 *  the browser POSTs a tool_result via sendAiToolResults so the chain
 *  resumes — AI sees `{selected_value, selected_label, free_text?}`
 *  and continues with the answer in mind. Mirrors the QuickNav
 *  pattern: live-rendered during the stream + re-extractable from
 *  tool_calls JSON for chat-history re-renders. */
/** propose_questions — 1-4 questions, each with 2-6 options, single-
 *  or multi-select. Operator submits all answers in one go; the
 *  tool_result carries an `answers` map keyed by `question.question`
 *  text. */
export interface QuestionOption {
  /** Stable token returned in the answer. */
  value: string;
  /** zh-TW button label shown to the operator. */
  label: string;
  /** Optional 1-line secondary text rendered beneath the label in card
   *  mode. When ANY option carries description/icon, that question's
   *  row switches from compact buttons to cards. */
  description?: string;
  /** Optional icon hint; renderer maps to a lucide icon, defaults to a
   *  neutral chevron when unset or unknown. */
  icon?: string;
}
export interface Question {
  /** Decision text rendered as the card title. */
  question: string;
  /** Short chip label shown top-left of the card (≤12 chars). */
  header: string;
  /** Single-select (radio, default) or multi-select (checkbox). */
  multiSelect: boolean;
  /** Whether the card appends a「其他」button + free-text input. */
  allow_other: boolean;
  /** Optional 1-line context rendered above the question. */
  reason: string;
  /** 2-6 concrete options (excluding the auto-appended「其他」). */
  options: QuestionOption[];
}
export interface QuestionsProposal {
  call_id: string;
  questions: Question[];
}
/** Per-question answer the operator picked on a propose_questions
 *  card. multiSelect questions return an array of these; single-select
 *  returns one. `free_text` populated only when the operator picked
 *  the auto-appended「其他」option. */
export interface QuestionAnswer {
  value: string;
  label: string;
  free_text?: string;
}
/** Tool result shape for propose_questions. Keyed by the question
 *  text exactly as the model wrote it, so the model can self-correlate
 *  without index bookkeeping. */
export type QuestionsAnswerMap = Record<string, QuestionAnswer | QuestionAnswer[]>;

/** Inter-op `$ref` placeholder (spec docs/specs/ai-plan-and-apply.md §5.11).
 *
 *  Any identifier field on a PlanOperation may be either a literal
 *  ULID string, OR a $ref object pointing at an earlier op's result.
 *  Dispatcher resolves `$ref` to the actual id after the referenced
 *  op completes; failed parent → child auto-skip with
 *  errorCode='unresolved_reference'.
 *
 *  Backward-only: `$op_idx` must be < the current op's index.
 *  Forward / self refs are rejected by dispatcher validation. */
export interface OpRef {
  /** 0-indexed; references plan.operations[$op_idx] */
  $op_idx: number;
  /** Result path to extract. Only "id" supported in Phase 4. */
  $path: "id";
}

/** Either a literal ULID string or a $ref to an earlier op. */
export type IdRef = string | OpRef;

export function isOpRef(v: unknown): v is OpRef {
  return (
    typeof v === "object" && v !== null &&
    typeof (v as { $op_idx?: unknown }).$op_idx === "number" &&
    (v as { $path?: unknown }).$path === "id"
  );
}

/** One operation inside a Plan. Matches the discriminated union in
 *  `apps/ai-api/src/tools.ts propose_plan parameters`. Client-side
 *  dispatcher narrows on `kind` to dispatch the right API call.
 *
 *  Spec: docs/specs/ai-plan-and-apply.md §5.2 (Phase 1) + §5.4 (Phase 2).
 *  Adding new kinds is a spec extension — don't grow this type without
 *  updating both sides. */
export type PlanOperation =
  // ── Phase 1: pure-create ops ───────────────────────────────────────
  | {
      kind: "create_task_assignment";
      display: string;
      task_id: IdRef;
      location_id: IdRef;
      route_id?: IdRef | null;
      rrule: string;
      duration_minutes?: number;
      grace_before_min?: number;
      grace_after_min?: number;
      valid_from: number;
      valid_to?: number | null;
      target_kind: "role" | "officer";
      target_value: string;
    }
  | {
      kind: "create_location";
      display: string;
      site_id: IdRef;
      code: string;
      label: string;
      risk_level?: "low" | "medium" | "high";
      lat?: number;
      lon?: number;
      geofence_radius_m?: number;
    }
  | {
      kind: "create_route";
      display: string;
      site_id: IdRef;
      name: string;
      cadence_minutes?: number;
      expected_duration_seconds?: number;
      locations?: { location_id: IdRef; expected_dwell_seconds?: number }[];
    }
  // ── Phase 2: 1 create + 4 patch ops ────────────────────────────────
  | {
      kind: "create_task";
      display: string;
      name: string;
      description_short?: string | null;
      requires_text?: boolean;
      requires_camera?: boolean;
      requires_file?: boolean;
      verify_device?: boolean;
      verify_face?: boolean;
      verify_geo?: boolean;
      requires_signature?: boolean;
      allow_skip?: boolean;
    }
  | {
      kind: "patch_route";
      display: string;
      route_id: IdRef;
      changes: PatchRouteChanges;
      before: PatchRouteBefore;
    }
  | {
      kind: "patch_task";
      display: string;
      task_id: IdRef;
      changes: PatchTaskChanges;
      before: PatchTaskBefore;
    }
  | {
      kind: "patch_task_assignment";
      display: string;
      assignment_id: IdRef;
      changes: PatchAssignmentChanges;
      before: PatchAssignmentBefore;
    }
  | {
      kind: "patch_location";
      display: string;
      location_id: IdRef;
      changes: PatchLocationChanges;
      before: PatchLocationBefore;
    }
  // ── Phase 3: Client / Site / Building / Floor onboarding ────────────
  | {
      kind: "create_client";
      display: string;
      name: string;
      contact_name?: string | null;
      contact_email?: string | null;
      contact_phone?: string | null;
      status?: "active" | "inactive";
    }
  | {
      kind: "patch_client";
      display: string;
      client_id: IdRef;
      changes: PatchClientChanges;
      before: PatchClientBefore;
    }
  | {
      kind: "create_site";
      display: string;
      client_id: IdRef;
      name: string;
      address?: string | null;
      timezone?: string;
      risk_level?: "low" | "medium" | "high";
      status?: "active" | "inactive";
    }
  | {
      kind: "patch_site";
      display: string;
      site_id: IdRef;
      changes: PatchSiteChanges;
      before: PatchSiteBefore;
    }
  | {
      kind: "create_building";
      display: string;
      site_id: IdRef;
      name: string;
      display_order?: number;
      floors_count?: number;
      floors_below_ground_count?: number;
      brand_primary?: string;
      description?: string;
      set_as_default?: boolean;
    }
  | {
      kind: "patch_building";
      display: string;
      building_id: IdRef;
      changes: PatchBuildingChanges;
      before: PatchBuildingBefore;
    }
  | {
      kind: "create_floor";
      display: string;
      building_id: IdRef;
      floor_no: number;
      floor_name: string;
      display_label?: string;
      display_order?: number;
      is_critical?: boolean;
    }
  | {
      kind: "patch_floor";
      display: string;
      floor_id: IdRef;
      changes: PatchFloorChanges;
      before: PatchFloorBefore;
    }
  // ── Phase 4: 班別 / 班表（schedule） ───────────────────────────────
  | {
      kind: "create_shift";
      display: string;
      name: string;
      start_time: string;       // "HH:MM"
      end_time: string;         // "HH:MM" (may be < start_time for overnight)
      days_mask: number;        // bitmask Mon=1..Sun=64
      officer_count?: number;
      status?: "active" | "inactive";
    }
  | {
      kind: "patch_shift";
      display: string;
      shift_id: IdRef;
      changes: PatchShiftChanges;
      before: PatchShiftBefore;
    }
  | {
      kind: "create_roster";
      display: string;
      shift_id: IdRef;
      officer_id: IdRef;
      site_id: IdRef;
      work_date: string;        // "YYYY-MM-DD"
      notes?: string;
      status?: "scheduled" | "confirmed";
    }
  | {
      kind: "patch_roster";
      display: string;
      roster_id: IdRef;
      changes: PatchRosterChanges;
      before: PatchRosterBefore;
    }
  // ── Phase 5: officer ↔ site assignments (REPLACE semantics) ──────────
  //
  // Backend `PUT /v1/admin/officers/:id/sites { site_ids }` does a full
  // REPLACE — any site not in `site_ids` gets removed. AI must list the
  // officer's current sites via list_officers (which includes assigned
  // sites) BEFORE proposing this op, then submit the FINAL set (current
  // + additions, minus removals). `before.site_names` carries the prior
  // assignment list so the modal renders a「修改前 → 修改後」 diff.
  | {
      kind: "assign_officer_sites";
      display: string;
      officer_id: IdRef;
      site_ids: IdRef[];
      before: {
        officer_name: string;
        site_names: string[];
      };
    }
  // ── Phase 6: Custom RBAC role + assignment ops (spec custom-rbac.md §7)
  //
  // Backend surface:
  //   create_role           → POST   /v1/admin/roles + PUT /roles/:id/permissions
  //   update_role           → PATCH  /v1/admin/roles/:id (+ optional PUT permissions)
  //   delete_role           → DELETE /v1/admin/roles/:id  (high-stakes; ack-gated in modal)
  //   assign_role_to_user   → PUT    /v1/admin/users/:userId/roles (append semantics)
  //   revoke_role_from_user → PUT    /v1/admin/users/:userId/roles (filter semantics)
  //
  // High-stakes ops (delete_role + revoke_role_from_user) require the
  // operator to tick a「我了解此操作不可復原」 ack before「套用」 enables.
  // System role mutation is server-rejected (`system_role_immutable`).
  | {
      kind: "create_role";
      display: string;
      name: string;
      description?: string | null;
      cloned_from?: string | null;       // optional system role id (e.g. "sys_manager")
      permission_ids: string[];           // full final list — NOT a diff
    }
  | {
      kind: "update_role";
      display: string;
      role_id: IdRef;
      changes: UpdateRoleChanges;
      before: UpdateRoleBefore;
    }
  | {
      kind: "delete_role";
      display: string;
      role_id: IdRef;
      before: {
        name: string;
        assigned_user_count: number;
      };
    }
  | {
      kind: "assign_role_to_user";
      display: string;
      user_id: string;
      role_id: IdRef;
      scope?: { sites?: string[]; buildings?: string[]; clients?: string[] } | null;
      before: {
        user_name: string;
        role_name: string;
        current_role_count: number;
      };
    }
  | {
      kind: "revoke_role_from_user";
      display: string;
      user_id: string;
      role_id: IdRef;
      before: {
        user_name: string;
        role_name: string;
        remaining_role_count: number;     // count AFTER revoke — 0 = will lose all roles
      };
    };

// ── Phase 2 patch-op nested types ────────────────────────────────────
//
// Spec §5.4 / §5.5: `before` snapshot is populated by AI from list_*/
// get_* read tools and consumed by PlanModal's diff view; `changes`
// is the partial PATCH body the dispatcher sends. Identifier-display
// fields (route name, task_name + location_label, location code+
// label) are always required on `before` so the modal can render
// 「正在修改：{識別}」 even when those fields aren't in `changes`.

export interface PatchRouteChanges {
  name?: string;
  cadence_minutes?: number | null;
  expected_duration_seconds?: number | null;
  status?: "active" | "archived";
}
export interface PatchRouteBefore extends PatchRouteChanges {
  name: string;
}

export interface PatchTaskChanges {
  name?: string;
  description_short?: string | null;
  requires_text?: boolean;
  requires_camera?: boolean;
  requires_file?: boolean;
  verify_device?: boolean;
  verify_face?: boolean;
  verify_geo?: boolean;
  requires_signature?: boolean;
  allow_skip?: boolean;
  status?: "active" | "archived";
}
export interface PatchTaskBefore extends PatchTaskChanges {
  name: string;
}

export interface PatchAssignmentChanges {
  location_id?: IdRef;
  route_id?: IdRef | null;
  rrule?: string;
  duration_minutes?: number;
  grace_before_min?: number;
  grace_after_min?: number;
  valid_from?: number;
  valid_to?: number | null;
  target_kind?: "role" | "officer";
  target_value?: string;
  status?: "active" | "archived";
}
export interface PatchAssignmentBefore extends PatchAssignmentChanges {
  /** Denormalised label fields populated by AI from
   *  `list_task_assignments` + `list_tasks` / `list_locations`.
   *  Not patchable — purely for the modal's identification line. */
  task_name: string;
  location_label: string;
}

export interface PatchLocationChanges {
  code?: string;
  label?: string;
  location_hint?: string | null;
  risk_level?: "low" | "medium" | "high";
  status?: "active" | "retired";
  lat?: number | null;
  lon?: number | null;
  geofence_radius_m?: number;
}
export interface PatchLocationBefore extends PatchLocationChanges {
  code: string;
  label: string;
}

// ── Phase 3 nested types ────────────────────────────────────────────

export interface PatchClientChanges {
  name?: string;
  contact_name?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  status?: "active" | "inactive";
}
export interface PatchClientBefore extends PatchClientChanges {
  name: string;
}

export interface PatchSiteChanges {
  /** Note: changing client_id moves the site under a different client.
   *  Cascades into reports / billing — AI should warn the operator in
   *  the plan summary. */
  client_id?: IdRef;
  name?: string;
  address?: string | null;
  timezone?: string;
  risk_level?: "low" | "medium" | "high";
  status?: "active" | "inactive";
}
export interface PatchSiteBefore extends PatchSiteChanges {
  name: string;
}

export interface PatchBuildingChanges {
  name?: string;
  display_order?: number;
  floors_count?: number;
  floors_below_ground_count?: number;
  brand_primary?: string | null;
  description?: string | null;
  status?: "active" | "archived";
}
export interface PatchBuildingBefore extends PatchBuildingChanges {
  name: string;
}

export interface PatchFloorChanges {
  /** floor_no (樓層編號) is NOT patchable — it's the identity. To
   *  renumber a floor, archive the old + create new. Mirrored from
   *  apps/tenant-api/src/floor-stack.ts PATCH /v1/floors/:id body. */
  floor_name?: string;
  display_label?: string | null;
  display_order?: number;
  is_critical?: boolean;
  status?: "active" | "disabled" | "archived";
}
export interface PatchFloorBefore extends PatchFloorChanges {
  floor_name: string;
}

// ── Phase 4 nested types ─────────────────────────────────────────────

export interface PatchShiftChanges {
  name?: string;
  start_time?: string;
  end_time?: string;
  days_mask?: number;
  officer_count?: number;
  status?: "active" | "inactive";
}
export interface PatchShiftBefore extends PatchShiftChanges {
  name: string;
}

export interface PatchRosterChanges {
  /** officer_id is patchable (re-assign to another officer); shift_id /
   *  site_id / work_date are server-immutable — to move a roster row
   *  cancel + create new. Mirrors apps/schedule-api/src/roster.ts PATCH
   *  body shape. */
  officer_id?: IdRef;
  notes?: string | null;
  status?: "scheduled" | "confirmed" | "swapped" | "cancelled";
}
export interface PatchRosterBefore extends PatchRosterChanges {
  /** Denormalised labels populated from list_schedule_roster — for the
   *  modal title「正在修改排班：{officer_name} ・ {work_date} ・ {shift_name}」. */
  officer_name: string;
  shift_name: string;
  work_date: string;
}

// ── Phase 6 nested types ────────────────────────────────────────────

export interface UpdateRoleChanges {
  name?: string;
  description?: string | null;
  /** Permissions to add to the role's existing set. */
  add_permission_ids?: string[];
  /** Permissions to remove from the role's existing set. */
  remove_permission_ids?: string[];
}

export interface UpdateRoleBefore {
  /** Display name — required so the modal can render「正在編輯角色：X」. */
  name: string;
  /** Current permission count — gives operator a sense of scale + drives
   *  the「N → N+add-remove」 hint in the diff view. */
  permission_count: number;
  description?: string | null;
}

export interface PlanPreflightError {
  op_index: number;
  op_kind: string;
  field: string;
  entity_id: string;
  reason: "not_found" | "inactive" | "uniqueness_conflict";
  detail?: string;
}

export interface PlanProposal {
  /** function_call id; reported back as tool_call_id when posting the
   *  per-op outcome bundle to /v1/ai/threads/:id/tool-result. */
  call_id: string;
  /** ai-api-generated ULID; client-side dispatcher sends this as the
   *  `x-plan-correlation-id` header on EVERY op's request so all the
   *  resulting audit rows share a group id back to /dashboard/audit. */
  plan_correlation_id: string;
  title: string;
  summary: string;
  operations: PlanOperation[];
  /** Server-side dry-run errors. When non-empty, PlanModal disables the
   *  「套用」 button and renders per-op red badges so the operator sees
   *  what failed before AI re-plans. */
  preflight_errors?: PlanPreflightError[];
}

export interface SendMessageEvents {
  onToken?: (text: string) => void;
  /** AI is about to call a tool. Show a spinner / status indicator. */
  onToolCallStarted?: (info: { call_id: string; name: string }) => void;
  /** AI emitted a fully-assembled function call. Browser should execute
   *  and POST the result back via sendAiToolResults(). */
  onToolCall?: (call: SseToolCall) => void;
  /** AI proposed a step-by-step UI tour. Show confirm modal; user
   *  accepts → tour engine runs → eventually POSTs outcome back as a
   *  tool_result. */
  onProposeTour?: (proposal: AiTourProposal) => void;
  /** AI emitted inline shortcut buttons. Render them on the streaming
   *  message bubble. Fire-and-forget — no tool_result expected. */
  onProposeQuickNav?: (proposal: QuickNavProposal) => void;
  /** AI proposed a batch plan. Open PlanModal so the user can review
   *  + apply. Client dispatcher posts the per-op outcomes back as a
   *  tool_result so AI can acknowledge. */
  onProposePlan?: (proposal: PlanProposal) => void;
  /** AM-5c — AI focused the AI 導覽 drawer on a subset of entities.
   *  Browser navigates / updates URL so the topology canvas filters
   *  to focused ids + their ancestor chain. Fire-and-forget — no
   *  tool_result expected. */
  onProposeFocusTopology?: (proposal: FocusTopologyProposal) => void;
  /** AM-7 — AI opened a create-form in the AI 導覽 drawer. Browser
   *  navigates / updates URL so the drawer renders the form; on save
   *  or cancel the browser posts a tool_result back so the AI can
   *  acknowledge ("已建立地點 X，要不要也派發任務？"). */
  onProposeDrawerForm?: (proposal: DrawerFormProposal) => void;
  /** AI proposed a stack of 1-4 picker cards. Render one card per
   *  question; operator picks across all questions and submits once.
   *  POST the answer map back as a tool_result so the chain resumes
   *  with all answers in hand. */
  onProposeQuestions?: (proposal: QuestionsProposal) => void;
  onFinal?: (info: SseFinal) => void;
  onError?: (info: { code: string; message: string }) => void;
}

/**
 * SSE stream consumer. Resolves when the stream ends (final event or
 * the underlying fetch closes). Rejects only on non-2xx HTTP responses
 * before the stream begins; per-event errors are delivered to onError
 * and DO NOT reject the promise.
 *
 * `AbortSignal` lets callers cancel a streaming reply mid-flight (e.g.
 * user pressed the "stop" button or navigated away). The underlying
 * fetch is aborted and the reader closes cleanly.
 */
export async function sendAiMessage(
  req: SendMessageRequest,
  events: SendMessageEvents = {},
  signal?: AbortSignal,
): Promise<void> {
  const r = await apiFetch(`/v1/ai/threads/${encodeURIComponent(req.threadId)}/messages`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json", accept: "text/event-stream" },
    body: JSON.stringify({
      text: req.text,
      screen_context: req.screen_context ?? null,
      lang_hint: req.langHint ?? null,
    }),
    signal,
  });
  if (!r.ok) throw await asError(r);
  if (!r.body) {
    events.onError?.({ code: "no_body", message: "Server returned empty stream" });
    return;
  }

  const reader = r.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";

  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      // SSE framing: events separated by blank line (\n\n).
      let sepIdx = buf.indexOf("\n\n");
      while (sepIdx >= 0) {
        const block = buf.slice(0, sepIdx);
        buf = buf.slice(sepIdx + 2);
        sepIdx = buf.indexOf("\n\n");
        dispatchSseBlock(block, events);
      }
    }
  } finally {
    reader.releaseLock();
  }
}

function dispatchSseBlock(block: string, events: SendMessageEvents): void {
  let eventName = "message";
  let data = "";
  for (const line of block.split("\n")) {
    if (line.startsWith("event:")) eventName = line.slice(6).trim();
    else if (line.startsWith("data:")) data += line.slice(5).trim();
    // ignore comments + other fields (id:, retry:)
  }
  if (!data) return;
  let parsed: unknown;
  try { parsed = JSON.parse(data); }
  catch { return; }

  switch (eventName) {
    case "token": {
      const p = parsed as { text?: string };
      if (typeof p.text === "string") events.onToken?.(p.text);
      break;
    }
    case "tool_call_started": {
      const p = parsed as { call_id?: string; name?: string };
      if (p.call_id && p.name) {
        events.onToolCallStarted?.({ call_id: p.call_id, name: p.name });
      }
      break;
    }
    case "tool_call": {
      const p = parsed as { call_id?: string; name?: string; args?: Record<string, unknown>; known?: boolean };
      if (p.call_id && p.name) {
        events.onToolCall?.({
          call_id: p.call_id,
          name: p.name,
          args: p.args ?? {},
          known: p.known !== false,
        });
      }
      break;
    }
    case "propose_tour": {
      const p = parsed as {
        call_id?: string;
        run_id?: string;
        title?: string;
        summary?: string;
        steps?: AiTourStep[];
      };
      if (p.call_id && p.run_id && Array.isArray(p.steps)) {
        events.onProposeTour?.({
          call_id: p.call_id,
          run_id: p.run_id,
          title: p.title ?? "",
          summary: p.summary ?? "",
          steps: p.steps,
        });
      }
      break;
    }
    case "quick_nav": {
      const p = parsed as { call_id?: string; items?: QuickNavItem[] };
      if (p.call_id && Array.isArray(p.items)) {
        events.onProposeQuickNav?.({ call_id: p.call_id, items: p.items });
      }
      break;
    }
    case "propose_plan": {
      const p = parsed as {
        call_id?: string;
        plan_correlation_id?: string;
        title?: string;
        summary?: string;
        operations?: PlanOperation[];
        preflight_errors?: PlanPreflightError[];
      };
      if (p.call_id && p.plan_correlation_id && Array.isArray(p.operations)) {
        events.onProposePlan?.({
          call_id: p.call_id,
          plan_correlation_id: p.plan_correlation_id,
          title: p.title ?? "",
          summary: p.summary ?? "",
          operations: p.operations,
          ...(Array.isArray(p.preflight_errors) && p.preflight_errors.length > 0
            ? { preflight_errors: p.preflight_errors }
            : {}),
        });
      }
      break;
    }
    case "propose_drawer_form": {
      const p = parsed as { call_id?: string; kind?: string; prefill?: unknown; reason?: string };
      // Whitelist by kind — keep this in sync with `DrawerFormKind` above
      // and DrawerFormHost's switch on the frontend. New kinds added in
      // tools.ts must also be added here or the runtime side-effect
      // (opening the drawer) silently drops.
      const VALID_KINDS = ["create_location", "create_client", "create_task", "issue_binding_code"];
      if (p.call_id && p.kind && VALID_KINDS.includes(p.kind)) {
        events.onProposeDrawerForm?.({
          call_id: p.call_id,
          kind: p.kind as DrawerFormKind,
          prefill: (p.prefill && typeof p.prefill === "object" ? p.prefill : {}) as Record<string, unknown>,
          reason: typeof p.reason === "string" ? p.reason : "",
        });
      }
      break;
    }
    case "propose_questions": {
      // Multi-question picker. Server forwarded args verbatim; we
      // sanitize+cap here defensively so a malformed payload can't
      // blow up React state.
      const p = parsed as { call_id?: string; questions?: unknown };
      if (!p.call_id || !Array.isArray(p.questions)) break;
      const questions: Question[] = [];
      for (const rawQ of p.questions) {
        if (!rawQ || typeof rawQ !== "object") continue;
        const q = rawQ as {
          question?: unknown;
          header?: unknown;
          multiSelect?: unknown;
          allow_other?: unknown;
          reason?: unknown;
          options?: unknown;
        };
        if (typeof q.question !== "string" || q.question.length === 0) continue;
        const header = typeof q.header === "string" ? q.header.slice(0, 12) : "";
        if (header.length === 0) continue;
        const rawOpts = Array.isArray(q.options) ? q.options : [];
        const options: QuestionOption[] = [];
        for (const r of rawOpts) {
          if (!r || typeof r !== "object") continue;
          const o = r as { value?: unknown; label?: unknown; description?: unknown; icon?: unknown };
          if (typeof o.value !== "string" || o.value.length === 0) continue;
          if (typeof o.label !== "string" || o.label.length === 0) continue;
          const opt: QuestionOption = { value: o.value.slice(0, 60), label: o.label.slice(0, 60) };
          if (typeof o.description === "string" && o.description.length > 0) {
            opt.description = o.description.slice(0, 120);
          }
          if (typeof o.icon === "string" && /^[a-z_]+$/.test(o.icon) && o.icon.length <= 32) {
            opt.icon = o.icon;
          }
          options.push(opt);
          if (options.length >= 6) break;
        }
        if (options.length < 2) continue; // schema requires ≥2 options
        questions.push({
          question: q.question.slice(0, 240),
          header,
          multiSelect: q.multiSelect === true,
          allow_other: q.allow_other !== false,
          reason: typeof q.reason === "string" ? q.reason.slice(0, 240) : "",
          options,
        });
        if (questions.length >= 4) break;
      }
      if (questions.length === 0) break;
      events.onProposeQuestions?.({
        call_id: p.call_id,
        questions,
      });
      break;
    }
    case "propose_focus_topology": {
      const p = parsed as { call_id?: string; entity_ids?: unknown; reason?: string };
      if (p.call_id && Array.isArray(p.entity_ids)) {
        const ids = (p.entity_ids as unknown[])
          .filter((v): v is string => typeof v === "string" && v.length > 0);
        if (ids.length > 0) {
          events.onProposeFocusTopology?.({
            call_id: p.call_id,
            entity_ids: ids,
            reason: typeof p.reason === "string" ? p.reason : "",
          });
        }
      }
      break;
    }
    case "final": {
      const p = parsed as {
        message_id?: string;
        user_message_id?: string | null;
        content?: string;
        tool_calls?: SseToolCall[];
        pending_tool_calls?: boolean;
        tokens_in?: number;
        tokens_out?: number;
        model?: string;
        response_id?: string | null;
      };
      if (p.message_id && typeof p.content === "string") {
        events.onFinal?.({
          message_id: p.message_id,
          user_message_id: p.user_message_id ?? null,
          content: p.content,
          tool_calls: p.tool_calls ?? [],
          pending_tool_calls: p.pending_tool_calls ?? false,
          tokens_in: p.tokens_in ?? 0,
          tokens_out: p.tokens_out ?? 0,
          model: p.model,
          response_id: p.response_id ?? null,
        });
      }
      break;
    }
    case "error": {
      const p = parsed as { code?: string; message?: string };
      events.onError?.({ code: p.code ?? "unknown", message: p.message ?? "" });
      break;
    }
  }
}

// ─── Tool-result resume ────────────────────────────────────────────────────

export interface ToolResultPayload {
  call_id: string;
  output: unknown;
}

/** POST executed tool results back to the AI to resume the chain.
 *  Returns the same SSE event stream as sendAiMessage — including
 *  potential follow-up tool_call events (AI may chain multiple). */
export async function sendAiToolResults(
  threadId: string,
  results: ToolResultPayload[],
  events: SendMessageEvents = {},
  signal?: AbortSignal,
): Promise<void> {
  const r = await apiFetch(`/v1/ai/threads/${encodeURIComponent(threadId)}/tool-result`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json", accept: "text/event-stream" },
    body: JSON.stringify({ tool_results: results }),
    signal,
  });
  if (!r.ok) throw await asError(r);
  if (!r.body) {
    events.onError?.({ code: "no_body", message: "Server returned empty stream" });
    return;
  }
  const reader = r.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      let sepIdx = buf.indexOf("\n\n");
      while (sepIdx >= 0) {
        const block = buf.slice(0, sepIdx);
        buf = buf.slice(sepIdx + 2);
        sepIdx = buf.indexOf("\n\n");
        dispatchSseBlock(block, events);
      }
    }
  } finally {
    reader.releaseLock();
  }
}

// ─── Tour lifecycle ────────────────────────────────────────────────────────

export interface TourStartBody {
  thread_id: string;
  message_id: string;
  title: string;
  steps: AiTourStep[];
  call_id: string;
}

export async function startTourRun(runId: string, body: TourStartBody): Promise<void> {
  const r = await apiFetch(`/v1/ai/tours/${encodeURIComponent(runId)}/start`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!r.ok) throw await asError(r);
}

export async function logTourStep(
  runId: string,
  step_index: number,
  outcome: "completed" | "skipped" | "failed",
  failure_reason?: string,
): Promise<void> {
  // Fire-and-forget — UI continues whether or not this lands. Step
  // logging is for audit / analytics; missing rows don't break the tour.
  try {
    await apiFetch(`/v1/ai/tours/${encodeURIComponent(runId)}/step`, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ step_index, outcome, failure_reason }),
    });
  } catch {
    /* swallow */
  }
}

export interface TourCompleteResult {
  id: string;
  tour_title: string;
  steps_planned: number;
  steps_completed: number;
  outcome: "completed" | "cancelled" | "failed" | "paused";
  failure_reason: string | null;
  started_at: string;
  ended_at: string | null;
}

export async function completeTourRun(
  runId: string,
  outcome: "completed" | "cancelled" | "failed" | "paused",
  failure_reason?: string,
): Promise<TourCompleteResult> {
  const r = await apiFetch(`/v1/ai/tours/${encodeURIComponent(runId)}/complete`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ outcome, failure_reason }),
  });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as TourCompleteResult;
}

// ─── Long-term memory facts (PR #7) ──────────────────────────────────────

export type AiFactKind =
  | "preference"
  | "role_context"
  | "confusion"
  | "workflow_pref"
  | "tenant_setup_state";

export interface AiFactRow {
  id: string;
  kind: AiFactKind;
  /** English canonical form. Used by the AI translator at chat render
   *  time (so the model can phrase the fact in whatever lang the user
   *  is chatting in). Always present. */
  content: string;
  /** 繁體中文 (zh-TW) display string for the dashboard AI memory card.
   *  Always present for facts extracted after the 2026-05 bilingual
   *  upgrade (migration 0031); null for legacy facts that pre-date it. */
  content_zh: string | null;
  confidence: number;
  source_thread_id: string | null;
  created_at: string;
  updated_at: string;
  expires_at: string | null;
}

export async function listAiFacts(): Promise<AiFactRow[]> {
  const r = await apiFetch("/v1/ai/facts", { credentials: "include" });
  if (!r.ok) throw await asError(r);
  const body = (await r.json()) as { items: AiFactRow[] };
  return body.items ?? [];
}

export async function deleteAiFact(id: string): Promise<void> {
  const r = await apiFetch(`/v1/ai/facts/${encodeURIComponent(id)}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!r.ok) throw await asError(r);
}

export async function wipeAiFacts(): Promise<{ deleted: number }> {
  const r = await apiFetch("/v1/ai/facts", {
    method: "DELETE",
    credentials: "include",
  });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as { deleted: number };
}

// ─── Feedback / cancel / usage (PR #8) ──────────────────────────────────

export async function sendAiFeedback(
  messageId: string,
  sentiment: "up" | "down",
  comment?: string,
): Promise<void> {
  const r = await apiFetch(`/v1/ai/messages/${encodeURIComponent(messageId)}/feedback`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ sentiment, comment }),
  });
  if (!r.ok) throw await asError(r);
}

export async function cancelAiTurn(threadId: string): Promise<void> {
  const r = await apiFetch(`/v1/ai/threads/${encodeURIComponent(threadId)}/cancel`, {
    method: "POST",
    credentials: "include",
  });
  if (!r.ok) throw await asError(r);
}

export interface AiUsage {
  date: string;
  used_today: number;
  tokens_in_today: number;
  tokens_out_today: number;
  cap_today: number;
}

export async function getAiUsage(): Promise<AiUsage> {
  const r = await apiFetch("/v1/ai/usage", { credentials: "include" });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as AiUsage;
}
